import { Link } from 'react-router-dom';
import { ArrowLeft, Youtube, Code, Sparkles, Globe, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';

const Creator = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": {
      "@type": "Person",
      "name": "Krish",
      "alternateName": "futureframeai",
      "description": "Creator and developer of TempVault (SecureMailWS) - a free, secure, and private disposable email service. Passionate about building privacy-focused tools and AI-powered applications.",
      "url": "https://www.securemailws.com/creator",
      "sameAs": [
        "https://www.youtube.com/channel/UCe48wIPCSvoMjRYa6wysETw"
      ],
      "knowsAbout": [
        "Web Development",
        "Privacy Technology",
        "Artificial Intelligence",
        "Email Security",
        "React",
        "TypeScript"
      ],
      "founder": {
        "@type": "WebApplication",
        "name": "TempVault",
        "alternateName": "SecureMailWS",
        "url": "https://www.securemailws.com"
      }
    }
  };

  const creatorStructuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://www.securemailws.com/creator#krish",
    "name": "Krish",
    "alternateName": "futureframeai",
    "description": "Krish, also known as futureframeai, is the creator and developer of SecureMailWS (TempVault), a privacy-focused disposable email service.",
    "url": "https://www.securemailws.com/creator",
    "sameAs": [
      "https://www.youtube.com/channel/UCe48wIPCSvoMjRYa6wysETw"
    ],
    "worksFor": {
      "@type": "Organization",
      "name": "futureframeai"
    },
    "makesOffer": {
      "@type": "Offer",
      "itemOffered": {
        "@type": "WebApplication",
        "name": "TempVault - SecureMailWS",
        "url": "https://www.securemailws.com",
        "description": "Free, secure, and private disposable email service"
      }
    }
  };

  const projects = [
    {
      icon: Mail,
      title: 'TempVault / SecureMailWS',
      description: 'A free, secure, and private disposable email service that protects your inbox from spam.',
      url: 'https://www.securemailws.com',
    },
  ];

  const skills = [
    'React & TypeScript',
    'AI & Machine Learning',
    'Privacy Technology',
    'Full-Stack Development',
    'Cloud Infrastructure',
    'UI/UX Design',
  ];

  return (
    <>
      <Helmet>
        <title>Krish (futureframeai) - Creator of TempVault | SecureMailWS</title>
        <meta name="description" content="Meet Krish, also known as futureframeai - the creator and developer of TempVault (SecureMailWS), a free, secure disposable email service. Follow on YouTube for tech content." />
        <meta name="author" content="Krish (futureframeai)" />
        <meta property="og:title" content="Krish (futureframeai) - Creator of TempVault" />
        <meta property="og:description" content="Meet the creator behind SecureMailWS - Krish, also known as futureframeai." />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content="https://www.securemailws.com/creator" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Krish (futureframeai) - Creator of TempVault" />
        <meta name="twitter:description" content="Meet the creator behind SecureMailWS - Krish, also known as futureframeai." />
        <link rel="canonical" href="https://www.securemailws.com/creator" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(creatorStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen gradient-surface gradient-mesh flex flex-col">
        <Navbar />
        
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <div className="glass-strong rounded-3xl p-6 lg:p-10 glow-border">
            <div className="flex items-center gap-4 mb-8">
              <Link to="/">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="w-12 h-12 rounded-2xl gradient-bg flex items-center justify-center">
                <Code className="w-6 h-6 text-primary-foreground" />
              </div>
              <h1 className="text-3xl font-bold gradient-text">Created By</h1>
            </div>

            <div className="space-y-8">
              {/* Creator Profile */}
              <section className="glass rounded-2xl p-6 lg:p-8">
                <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                  <div className="w-24 h-24 rounded-full gradient-bg flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-12 h-12 text-primary-foreground" />
                  </div>
                  <div className="text-center md:text-left">
                    <h2 className="text-2xl font-bold mb-1">Krish</h2>
                    <p className="text-primary font-medium mb-3">@futureframeai</p>
                    <p className="text-muted-foreground leading-relaxed max-w-xl">
                      Hello! I'm Krish, also known as <strong>futureframeai</strong>. I'm a passionate developer 
                      focused on building privacy-first tools and AI-powered applications. I created 
                      <strong> TempVault (SecureMailWS)</strong> to help people protect their online privacy 
                      with free, secure disposable email addresses.
                    </p>
                  </div>
                </div>
              </section>

              {/* YouTube Channel */}
              <section>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Youtube className="w-6 h-6 text-red-500" />
                  YouTube Channel
                </h2>
                <a 
                  href="https://www.youtube.com/channel/UCe48wIPCSvoMjRYa6wysETw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block glass rounded-2xl p-5 hover-lift transition-all duration-300 group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-destructive/20 flex items-center justify-center group-hover:bg-destructive/30 transition-colors">
                      <Youtube className="w-7 h-7 text-destructive" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">futureframeai</h3>
                      <p className="text-sm text-muted-foreground">Subscribe for tech tutorials, AI content, and development insights</p>
                    </div>
                  </div>
                </a>
              </section>

              {/* Projects */}
              <section>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <Globe className="w-6 h-6 text-primary" />
                  Projects
                </h2>
                <div className="grid gap-4">
                  {projects.map((project) => (
                    <a
                      key={project.title}
                      href={project.url}
                      className="block glass rounded-2xl p-5 hover-lift transition-all duration-300 group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center flex-shrink-0">
                          <project.icon className="w-6 h-6 text-primary-foreground" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">{project.title}</h3>
                          <p className="text-sm text-muted-foreground">{project.description}</p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </section>

              {/* Skills */}
              <section>
                <h2 className="text-2xl font-bold mb-4">Skills & Expertise</h2>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill) => (
                    <span
                      key={skill}
                      className="px-4 py-2 rounded-full glass text-sm font-medium"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </section>

              {/* CTA */}
              <section className="glass rounded-2xl p-6 text-center">
                <h2 className="text-xl font-bold mb-3">Want to get in touch?</h2>
                <p className="text-muted-foreground mb-4">
                  Feel free to reach out through the contact page or subscribe to my YouTube channel!
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Link to="/contact">
                    <Button className="rounded-full px-6">
                      Contact Me
                    </Button>
                  </Link>
                  <a 
                    href="https://www.youtube.com/channel/UCe48wIPCSvoMjRYa6wysETw"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="rounded-full px-6 gap-2">
                      <Youtube className="w-4 h-4" />
                      Subscribe
                    </Button>
                  </a>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Creator;
